import React, { useRef, useState } from 'react';
import cn from 'classnames';
import { useOverlayContext } from 'seagull';
import { Spinner, Typography, TextButton } from 'seagull';
import { Plus } from 'seagull/icons';
import styles from "./LogoPicker.module.scss";
export function readLogo(file, onChange, onNonPNG, onFileTooBig) {
  const fileSizeLimit = 5 * 1024 * 1024;
  if (!file) {
    return;
  }
  if (!file.name.toLowerCase().endsWith('.png')) {
    return onNonPNG();
  }
  if (file.size >= fileSizeLimit) {
    return onFileTooBig();
  }
  const reader = new FileReader();
  reader.onload = e => {
    const data = e.target ? {
      file,
      asDataURL: e.target.result
    } : undefined;
    onChange(data);
  };
  reader.readAsDataURL(file);
}

/** Renders a circle with a plus icon that chooses a .png file from the file-system with a clear button. */
export const LogoPicker = props => {
  const {
    image,
    loading,
    testid,
    isNewUI,
    onChange
  } = props;
  const overlayContext = useOverlayContext();
  const logoInputRef = useRef(null);
  const [isDraggedOver, setIsDraggedOver] = useState(false);
  const readFile = file => readLogo(file, onChange, () => overlayContext.showErrorToast('Invalid file type: must be .png', 'file-type-error'), () => overlayContext.showErrorToast('Selected file is too large. Please select a smaller one.', 'file-size-error'));
  const onPickLogo = event => {
    if (!event.target.files || !event.target.files.length) {
      return;
    }
    readFile(event.target.files[0]);
  };
  const triggerInput = () => logoInputRef.current && logoInputRef.current.click();
  const onDrop = e => {
    e.preventDefault();
    if (e.dataTransfer.items.length && e.dataTransfer.items[0].kind === 'file') {
      const file = e.dataTransfer.items[0].getAsFile();
      readFile(file);
    }
    setIsDraggedOver(false);
  };
  const onDragOver = e => {
    e.preventDefault();
    setIsDraggedOver(true);
  };
  const onDragLeave = () => {
    setIsDraggedOver(false);
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    className: isNewUI ? cn(styles.uploadBox, {
      [styles.draggedOver]: isDraggedOver,
      [styles.hasLogo]: !!image
    }) : cn(styles.circle, {
      [styles.withBorder]: !image,
      [styles.draggedOver]: isDraggedOver
    }),
    onDrop: onDrop,
    onDragOver: onDragOver,
    onDragLeave: onDragLeave
  }, /*#__PURE__*/React.createElement("input", {
    className: styles.input,
    ref: logoInputRef,
    type: "file",
    accept: ".png",
    "data-testid": testid && "".concat(testid, "-input"),
    onChange: onPickLogo
  }), /*#__PURE__*/React.createElement("button", {
    className: styles.add,
    "data-testid": testid && "".concat(testid, "-add"),
    disabled: loading,
    onClick: triggerInput
  }, image ? /*#__PURE__*/React.createElement("img", {
    className: styles.image,
    alt: "Logo",
    src: image,
    "data-testid": testid && "".concat(testid, "-img")
  }) : isNewUI ? /*#__PURE__*/React.createElement(Typography, {
    variant: "paragraph2",
    skin: "secondary"
  }, "No logo") : /*#__PURE__*/React.createElement(Plus, null)), loading && /*#__PURE__*/React.createElement("div", {
    className: styles.loading
  }, /*#__PURE__*/React.createElement(Spinner, {
    size: 40
  }))), isNewUI ? /*#__PURE__*/React.createElement(Typography, {
    variant: "paragraph3",
    skin: "secondary"
  }, /*#__PURE__*/React.createElement(TextButton, {
    variant: "paragraph2",
    underline: "none",
    onClick: triggerInput
  }, image ? 'Change file' : 'Upload file'), " (PNG smaller than 5MB)") : /*#__PURE__*/React.createElement(Typography, {
    variant: "paragraph2",
    className: styles.uploadLogoBox
  }, "Drag and drop your document here or", ' ', /*#__PURE__*/React.createElement(TextButton, {
    variant: "paragraph2",
    onClick: triggerInput
  }, "select file")));
};